// js/components/HorizontalScrollSlider.js
export class HorizontalScrollSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.prevButton = null;
    this.nextButton = null;
    this.dots = [];
    this.currentIndex = 0;
    this.scrollTimeout = null;
    this.resizeTimeout = null;
    this.isScrolling = false;
    this.scrollbarTimeout = null; // For hiding scrollbar after scrolling stops
    this.autoPlayTimer = null;
    this.autoPlayPausedByHover = false;
    this.autoPlayPausedByEditor = false;
    this.boundMouseEnter = null;
    this.boundMouseLeave = null;
    this.boundFocusIn = null;
    this.boundFocusOut = null;
    this.boundVisibilityChange = null;
    this.boundTouchStartForAutoplay = null;
    this.boundTouchEndForAutoplay = null;
    this.boundUserInteractionHandler = null;
    
    // Configuration
    this.config = {
      scrollAmount: 250, // pixels to scroll per click (standardized)
      scrollSpeed: 300, // animation duration in ms
      slidesToScroll: 1, // number of slides to scroll per click
      showDots: false,
      hideControlsOnTouch: true,
      snapToItems: false,
      autoPlay: false,
      autoPlayInterval: 5000,
      pauseOnHover: true
    };
  }

  connectedCallback() {
    // Parse settings up-front so editor hooks (which depend on config.sectionId)
    // work even when init() early-returns because content currently fits without scroll.
    this.parseSettings();
    this.init();
    this.setupShopifyThemeEditor();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    // Get slider element
    const container = this.querySelector('.cos-slider');
    const content = container?.querySelector('.cos-slider__content');
    
    // Determine which element can scroll by checking overflow and scrollWidth
    let scrollableElement = null;
    
    if (container && content) {
      // Read geometry directly — scrollWidth/offsetWidth below already force a
      // synchronous layout, so an extra throwaway offsetHeight reflow is redundant.
      const containerStyle = getComputedStyle(container);
      const contentStyle = getComputedStyle(content);
      
      
      // Check which element can actually scroll
      if (container.scrollWidth > container.offsetWidth && (containerStyle.overflowX === 'auto' || containerStyle.overflowX === 'scroll')) {
        scrollableElement = container;
      } else if (content.scrollWidth > content.offsetWidth && (contentStyle.overflowX === 'auto' || contentStyle.overflowX === 'scroll')) {
        scrollableElement = content;
      } else {
        // Fallback: try content first, then container
        scrollableElement = content || container;
      }
    }
    
    this.slider = scrollableElement || container;
    
    if (!this.slider) {
      return;
    }
    
    // Check content-fits FIRST, while still in the read phase — this scrollWidth/
    // offsetWidth read stays batched with the reads above (no style write between),
    // so init does one layout pass instead of a write->read forced reflow. ×N sliders.
    if (this.slider.scrollWidth <= this.slider.offsetWidth) {
      return;
    }

    // Overflow write happens AFTER all init geometry reads (nothing reads layout
    // after it during setup) so it can't force a sync reflow. CSS already sets
    // overflow-x:auto on .cos-slider; this is a belt-and-suspenders fallback.
    if (this.slider === container) {
      this.slider.style.overflowX = 'auto';
      this.slider.style.overflowY = 'hidden';
    }
    
    // Prevent double initialization after content check
    if (this.classList.contains('cos-slider--initialized')) {
      return;
    }

    // Apply configuration from data attributes
    this.applyConfiguration();

    // Setup event listeners
    this.setupEventListeners();

    // Set initial height if not already set
    this.setInitialHeight();

    // Verificăm dacă există un block selectat în theme editor pentru a seta slide-ul inițial
    this.setInitialSlideFromEditor();

    // Mark as initialized to show controls if needed
    this.classList.add('cos-slider--initialized');
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.findAndSetupControls();
      this.setupAutoPlay();
    });
  }

  findAndSetupControls(retryCount = 0) {
    
    // Get control elements
    this.prevButton = this.querySelector('[data-scroll="prev"]');
    this.nextButton = this.querySelector('[data-scroll="next"]');
    this.dots = this.querySelectorAll('.cos-slider__dot');

    // READ geometry FIRST, on clean layout — before generateDotsIfNeeded() writes
    // ~11 dots into the DOM. Reading scrollWidth/offsetLeft AFTER that write forced
    // a reflow per slider (×5 on the homepage). Order is now reads → writes.
    this.recomputeNeedsScrolling();
    this.recomputeItemOffsets();

    // Generate dots if they don't exist but dots container exists (DOM write;
    // re-queries this.dots internally, so the gate below still sees them)
    this.generateDotsIfNeeded();

    // Only setup if we found controls
    if (this.prevButton || this.nextButton || this.dots.length > 0) {
      // Setup control event listeners
      this.setupControlEventListeners();

      // Initial setup (writes only — geometry was already read above)
      this.updateControls();
      this.updateDots();
      
    } else if (retryCount < 5) {
      
      // Try again after a short delay
      setTimeout(() => {
        this.findAndSetupControls(retryCount + 1);
      }, 100);
    }
  }

  generateDotsIfNeeded() {
    // Check if dots container exists but no dots are found
    const dotsContainer = this.querySelector('.cos-slider__dots');
    if (dotsContainer && this.dots.length === 0 && this.config.showDots) {
      const totalItems = this.getTotalItems();
      const items = this.getSliderItems();
      
      // Only generate dots if there are multiple items
      if (totalItems > 1) {
        // Clear any existing content
        dotsContainer.innerHTML = '';
        
        // Generate dots
        for (let i = 0; i < totalItems; i++) {
          const dot = document.createElement('button');
          dot.className = 'cos-slider__dot';
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.setAttribute('data-slide-index', i);
          
          // Check if this is product gallery slider and if slide is video
          const isProductGallery = this.classList.contains('product-gallery-slider');
          const slide = items[i];
          const isVideo = slide && (slide.dataset.mediaType === 'video' || slide.dataset.mediaType === 'external_video');
          
          if (isProductGallery && isVideo) {
            // Add video icon SVG for video slides
            dot.innerHTML = `
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" class="cos-slider__dot-icon">
                <path d="M3 9.56574V2.43426C3 2.03491 3.44507 1.79672 3.77735 2.01823L9.12596 5.58397C9.42283 5.78189 9.42283 6.21811 9.12596 6.41603L3.77735 9.98177C3.44507 10.2033 3 9.96509 3 9.56574Z" fill="currentColor"/>
              </svg>
            `;
          } else {
            // Create span inside button for regular dots
            const dotSpan = document.createElement('span');
            dotSpan.className = 'cos-slider__dot-inner';
            dot.appendChild(dotSpan);
          }
          
          dotsContainer.appendChild(dot);
        }
        
        // Re-query dots after generation
        this.dots = this.querySelectorAll('.cos-slider__dot');
        
        // Re-setup dots event listeners since we have new dots
        this.setupDotsEventListeners();
        
      }
    }
  }

  setupDotsEventListeners() {
    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.scrollToIndex(index);
        this.handleUserInteraction();
      });
    });
  }

  setupControlEventListeners() {
    
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.scrollPrev();
        this.handleUserInteraction();
      });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.scrollNext();
        this.handleUserInteraction();
      });
    }

    // Dots navigation
    this.setupDotsEventListeners();
  }

  parseSettings() {
    // Pure data parsing — no DOM side effects. Safe to call before this.slider exists.
    let config = {};

    if (this.dataset.settings) {
      try {
        config = JSON.parse(this.dataset.settings);
      } catch (e) {
      }
    } else {
      // Fallback to individual data attributes for backward compatibility
      config = {
        scrollAmount: parseInt(this.dataset.scrollAmount) || this.config.scrollAmount,
        scrollSpeed: parseInt(this.dataset.scrollSpeed) || this.config.scrollSpeed,
        slidesToScroll: parseInt(this.dataset.slidesToScroll) || this.config.slidesToScroll,
        showDots: this.dataset.showDots !== 'false',
        hideControlsOnTouch: this.dataset.hideControlsOnTouch !== 'false',
        snapToItems: this.dataset.snapToItems === 'true',
        autoPlay: this.dataset.autoplay === 'true',
        autoPlayInterval: parseInt(this.dataset.autoplayInterval) || this.config.autoPlayInterval,
        pauseOnHover: this.dataset.pauseOnHover !== 'false'
      };
    }

    Object.assign(this.config, config);
    this.config.autoPlayInterval = parseInt(this.config.autoPlayInterval) || 5000;
  }

  applyConfiguration() {
    // DOM-side effects derived from parsed config. Requires this.slider to exist.
    this.setAttribute('data-show-dots', this.config.showDots.toString());

    if (this.config.snapToItems) {
      this.slider.classList.add('cos-slider--snap');
    }

    if (this.config.hideControlsOnTouch && this.isTouchDevice()) {
      const controls = this.querySelector('.cos-slider__controls');
      if (controls) {
        controls.style.display = 'none';
      }
    }
  }

  setupEventListeners() {

    // Scroll events
    this.boundScrollHandler = () => this.onScroll();
    this.slider.addEventListener('scroll', this.boundScrollHandler);

    // Resize events
    this.boundResizeHandler = () => this.onResize();
    window.addEventListener('resize', this.boundResizeHandler);

    this.boundUserInteractionHandler = () => this.handleUserInteraction();
    this.slider.addEventListener('pointerdown', this.boundUserInteractionHandler);
    this.slider.addEventListener('wheel', this.boundUserInteractionHandler, { passive: true });

    // Keyboard navigation (ArrowLeft / ArrowRight)
    this.boundKeydownHandler = (e) => this.onKeydown(e);
    this.addEventListener('keydown', this.boundKeydownHandler);
  }

  onKeydown(e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    // Don't hijack arrow keys when the user is editing a form field inside the slider.
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT' || active.isContentEditable)) {
      return;
    }

    e.preventDefault();
    if (e.key === 'ArrowLeft') {
      this.scrollPrev();
    } else {
      this.scrollNext();
    }
    this.handleUserInteraction();
  }

  handleUserInteraction() {
    if (!this.config.autoPlay) return;
    if (this.autoPlayPausedByHover || this.autoPlayPausedByEditor) return;
    this.startAutoPlay();
  }

  setupAutoPlay() {
    if (!this.config.autoPlay) return;
    if (!this.slider) return;

    const totalItems = this.getTotalItems();
    if (totalItems <= 1) return;

    this.autoPlayPausedByHover = false;
    this.autoPlayPausedByEditor = false;
    this.startAutoPlay();

    if (this.config.pauseOnHover) {
      this.boundMouseEnter = () => this.handleAutoPlayHoverStart();
      this.boundMouseLeave = () => this.handleAutoPlayHoverEnd();
      this.boundFocusIn = () => this.handleAutoPlayHoverStart();
      this.boundFocusOut = () => this.handleAutoPlayHoverEnd();

      this.addEventListener('mouseenter', this.boundMouseEnter);
      this.addEventListener('mouseleave', this.boundMouseLeave);
      this.addEventListener('focusin', this.boundFocusIn);
      this.addEventListener('focusout', this.boundFocusOut);

      if (this.slider) {
        this.boundTouchStartForAutoplay = () => this.handleAutoPlayHoverStart();
        this.boundTouchEndForAutoplay = () => this.handleAutoPlayHoverEnd();
        this.slider.addEventListener('touchstart', this.boundTouchStartForAutoplay, { passive: true });
        this.slider.addEventListener('touchend', this.boundTouchEndForAutoplay);
      }
    }

    this.boundVisibilityChange = () => this.handleVisibilityChange();
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
  }

  startAutoPlay() {
    if (!this.config.autoPlay) return;
    if (!this.slider) return;
    if (this.autoPlayPausedByHover || this.autoPlayPausedByEditor) return;

    const totalItems = this.getTotalItems();
    if (totalItems <= 1) return;

    this.stopAutoPlay();

    const interval = Math.max(2000, this.config.autoPlayInterval || 5000);
    this.autoPlayTimer = setInterval(() => this.handleAutoPlayAdvance(), interval);
  }

  stopAutoPlay() {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  handleAutoPlayAdvance() {
    if (!this.slider) return;
    if (this.autoPlayPausedByHover || this.autoPlayPausedByEditor) return;

    const totalItems = this.getTotalItems();
    if (totalItems <= 1) return;

    let nextIndex = this.currentIndex + this.config.slidesToScroll;
    if (nextIndex >= totalItems) {
      nextIndex = 0;
    }

    this.scrollToIndex(nextIndex);
  }

  handleAutoPlayHoverStart() {
    this.autoPlayPausedByHover = true;
    this.stopAutoPlay();
  }

  handleAutoPlayHoverEnd() {
    this.autoPlayPausedByHover = false;
    this.startAutoPlay();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopAutoPlay();
    } else {
      this.startAutoPlay();
    }
  }

  pauseAutoPlayForEditor() {
    if (!this.config.autoPlay) return;
    this.autoPlayPausedByEditor = true;
    this.stopAutoPlay();
  }

  resumeAutoPlayFromEditor() {
    if (!this.config.autoPlay) return;
    this.autoPlayPausedByEditor = false;
    this.startAutoPlay();
  }

  teardownAutoPlay() {
    this.stopAutoPlay();

    if (this.boundMouseEnter) {
      this.removeEventListener('mouseenter', this.boundMouseEnter);
      this.boundMouseEnter = null;
    }

    if (this.boundMouseLeave) {
      this.removeEventListener('mouseleave', this.boundMouseLeave);
      this.boundMouseLeave = null;
    }

    if (this.boundFocusIn) {
      this.removeEventListener('focusin', this.boundFocusIn);
      this.boundFocusIn = null;
    }

    if (this.boundFocusOut) {
      this.removeEventListener('focusout', this.boundFocusOut);
      this.boundFocusOut = null;
    }

    if (this.slider && this.boundTouchStartForAutoplay) {
      this.slider.removeEventListener('touchstart', this.boundTouchStartForAutoplay);
      this.boundTouchStartForAutoplay = null;
    }

    if (this.slider && this.boundTouchEndForAutoplay) {
      this.slider.removeEventListener('touchend', this.boundTouchEndForAutoplay);
      this.boundTouchEndForAutoplay = null;
    }

    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.boundVisibilityChange = null;
    }
  }



  scrollPrev() {
    const newIndex = Math.max(0, this.currentIndex - this.config.slidesToScroll);
    if (newIndex !== this.currentIndex) {
      this.scrollToIndex(newIndex);
    }
  }

  scrollNext() {
    const maxIndex = this.getTotalItems() - 1;
    const newIndex = Math.min(maxIndex, this.currentIndex + this.config.slidesToScroll);
    if (newIndex !== this.currentIndex) {
      this.scrollToIndex(newIndex);
    }
  }

  scrollToIndex(index) {
    const items = this.getSliderItems();
    
    if (index >= 0 && index < items.length) {
      this.currentIndex = index;
      const item = items[index];

      const itemLeft = item.offsetLeft;
      const itemWidth = item.offsetWidth;
      const sliderWidth = this.slider.offsetWidth;
      
      // Calculate scroll position to center the item or align it properly
      let scrollLeft = itemLeft;
      
      // If the item is wider than the slider, center it
      if (itemWidth > sliderWidth) {
        scrollLeft = itemLeft - (sliderWidth - itemWidth) / 2;
      }
      
      // Ensure we don't scroll beyond bounds
      scrollLeft = Math.max(0, Math.min(scrollLeft, this.slider.scrollWidth - sliderWidth));

      this.isScrolling = true;

      this.slider.scrollTo({ left: scrollLeft, behavior: 'smooth' });

      setTimeout(() => {
        this.isScrolling = false;
        this.updateControls();
        this.updateDots();
      }, this.config.scrollSpeed);
    }
  }

  onScroll() {
    // Show scrollbar when scrolling
    this.classList.add('cos-slider--scrolling');
    
    // Clear any existing timeout for hiding scrollbar
    if (this.scrollbarTimeout) {
      clearTimeout(this.scrollbarTimeout);
    }
    
    // Hide scrollbar after scrolling stops (after 800ms of inactivity)
    this.scrollbarTimeout = setTimeout(() => {
      this.classList.remove('cos-slider--scrolling');
    }, 800);
    
    // Throttle scroll events
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.updateCurrentIndex();
      this.updateControls();
      this.updateDots();
    }, 10);
    
    // Also update controls immediately for better responsiveness
    this.updateControls();
  }

  onResize() {
    // Clear any existing timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Update controls and dots after resize with longer delay for stability
    this.resizeTimeout = setTimeout(() => {
      this.recomputeNeedsScrolling();
      this.recomputeItemOffsets();
      this.updateCurrentIndex();
      this.updateControls();
      this.updateDots();
    }, 150);
  }

  updateControls() {
    if (!this.prevButton || !this.nextButton) return;

    const maxIndex = this.getTotalItems() - 1;
    const totalItems = this.getTotalItems();
    
    // Update button states
    this.prevButton.disabled = this.currentIndex <= 0;
    this.nextButton.disabled = this.currentIndex >= maxIndex;

    // Check if slider actually needs scrolling
    if (this._needsScrolling === undefined) this.recomputeNeedsScrolling();
    
    // Show controls only if there are multiple items AND scrolling is needed
    if (totalItems <= 1 || !this._needsScrolling) {
      this.classList.add('cos-slider--hide-controls');
    } else {
      this.classList.remove('cos-slider--hide-controls');
    }
  }


  // Single geometry read kept OFF the hot scroll path — recomputed only on
  // init and resize (scrollWidth/offsetWidth don't change while scrolling).
  recomputeNeedsScrolling() {
    this._needsScrolling = !!(this.slider && this.slider.scrollWidth > this.slider.offsetWidth);
  }

  // Cache each item's left/right edge — invariant to scroll, recomputed only on
  // init/resize. Lets updateCurrentIndex run on scroll without forcing layout.
  recomputeItemOffsets() {
    const items = this.getSliderItems();
    this._itemOffsets = [];
    for (let i = 0; i < items.length; i++) {
      const left = items[i].offsetLeft;
      this._itemOffsets.push({ left, right: left + items[i].offsetWidth });
    }
  }

  updateCurrentIndex() {
    // Item offsets are invariant to scroll — read them from cache (recomputed on
    // init/resize) so this scroll-driven fn does no layout-forcing geometry read.
    if (!this._itemOffsets) this.recomputeItemOffsets();
    const scrollLeft = this.slider.scrollLeft;

    for (let i = 0; i < this._itemOffsets.length; i++) {
      const o = this._itemOffsets[i];
      if (scrollLeft >= o.left && scrollLeft < o.right) {
        this.currentIndex = i;
        break;
      }
    }
  }

  updateDots() {
    if (!this.config.showDots || this.dots.length === 0) return;

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('cos-slider__dot--active', index === this.currentIndex);
    });
  }

  setInitialHeight() {
    // Height and width are now determined by content, no need to set fixed dimensions
    // Just ensure items are properly structured for content-based dimensions
    const items = this.getSliderItems();
    items.forEach(item => {
      // Remove any fixed dimensions to allow content to determine size
      if (item.style.height === '100%') {
        item.style.height = 'auto';
      }
      if (item.style.width && item.style.width !== 'auto') {
        item.style.width = 'auto';
      }
    });
    // No forced reflow here — the rAF in init() (findAndSetupControls) reads
    // geometry on the next frame, recalculating layout without a sync thrash.
  }

  setInitialSlideFromEditor() {
    // În Shopify theme editor, block-ul selectat se gestionează prin event-urile
    // shopify:block:select, nu prin detectare la inițializare
    // Slider-ul va naviga automat la block-ul selectat când admin-ul îl alege
  }

  isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  // Public methods for external control
  scrollToStart() {
    this.scrollToIndex(0);
  }

  scrollToEnd() {
    const maxIndex = this.getTotalItems() - 1;
    this.scrollToIndex(maxIndex);
  }

  getCurrentIndex() {
    return this.currentIndex;
  }

  getTotalItems() {
    const items = this.getSliderItems();
    return items.length;
  }

  getSliderItems() {
    // Try to find items with the old class first, then fallback to direct children
    let items = this.slider.querySelectorAll('.cos-slider__item');
    if (items.length === 0) {
      // Fallback to direct children of the content container
      const content = this.slider.querySelector('.cos-slider__content');
      if (content) {
        items = Array.from(content.children);
      } else {
        // Last fallback: direct children of slider
        items = Array.from(this.slider.children);
      }
    } else {
      // Convert NodeList to Array
      items = Array.from(items);
    }
    return items;
  }

  // Method to reinitialize (useful when content changes)
  reinitialize() {
    this.destroy();
    this.init();
  }


  destroy() {
    // Remove event listeners
    if (this.slider && this.boundScrollHandler) {
      this.slider.removeEventListener('scroll', this.boundScrollHandler);
      this.boundScrollHandler = null;
    }

    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
      this.boundResizeHandler = null;
    }

    if (this.slider && this.boundUserInteractionHandler) {
      this.slider.removeEventListener('pointerdown', this.boundUserInteractionHandler);
      this.slider.removeEventListener('wheel', this.boundUserInteractionHandler);
      this.boundUserInteractionHandler = null;
    }

    if (this.boundKeydownHandler) {
      this.removeEventListener('keydown', this.boundKeydownHandler);
      this.boundKeydownHandler = null;
    }

    this.teardownAutoPlay();
    
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Remove initialized class
    this.classList.remove('cos-slider--initialized');
  }

  // Shopify theme editor support
  setupShopifyThemeEditor() {
    // Only setup if we have a sectionId in config
    if (!this.config.sectionId) return;

    const sectionId = this.config.sectionId;
    
    // Setup event listeners for Shopify theme editor
    document.addEventListener('shopify:block:select', (event) => {
      const section = event.target.closest('.shopify-section');
      if (section && section.getAttribute('id') === `shopify-section-${sectionId}`) {
        this.onBlockSelect(event);
      }
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const section = event.target.closest('.shopify-section');
      if (section && section.getAttribute('id') === `shopify-section-${sectionId}`) {
        this.onBlockDeselect(event);
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.getAttribute('id') === `shopify-section-${sectionId}`) {
        this.onSectionLoad(event);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.getAttribute('id') === `shopify-section-${sectionId}`) {
        this.onSectionUnload();
      }
    });

    document.addEventListener('shopify:block:load', (event) => {
      const section = event.target.closest('.shopify-section');
      if (section && section.getAttribute('id') === `shopify-section-${sectionId}`) {
        this.onBlockLoad(event);
      }
    });
  }

  onBlockSelect(event) {
    // The event.target should be the block element that has {{ block.shopify_attributes }}
    let blockElement = event.target;
    let blockIndex = null;
    
    // Check if the target itself has data-block-index
    if (blockElement && blockElement.getAttribute) {
      blockIndex = parseInt(blockElement.getAttribute('data-block-index'));
    }
    
    // If not found, look for data-block-index in child elements
    if (isNaN(blockIndex) && blockElement) {
      const childWithIndex = blockElement.querySelector('[data-block-index]');
      if (childWithIndex) {
        blockIndex = parseInt(childWithIndex.getAttribute('data-block-index'));
      }
    }
    
    // If still not found, look for data-block-index in parent elements
    if (isNaN(blockIndex) && blockElement) {
      const parentWithIndex = blockElement.closest('[data-block-index]');
      if (parentWithIndex) {
        blockIndex = parseInt(parentWithIndex.getAttribute('data-block-index'));
      }
    }
    
    if (!isNaN(blockIndex) && blockIndex >= 0) {
      this.scrollToIndex(blockIndex);
      this.pauseAutoPlayForEditor();
    }
  }

  onBlockDeselect(event) {
    this.resumeAutoPlayFromEditor();
  }

  onSectionLoad(event) {
    // Salvăm poziția curentă pentru a o restaura după reinițializare
    const currentIndex = this.currentIndex;
    const currentScrollLeft = this.slider ? this.slider.scrollLeft : 0;
    
    this.reinitialize();
    
    // Restaurăm poziția dacă avem o poziție validă
    if (currentIndex > 0 && currentIndex < this.getTotalItems()) {
      this.scrollToIndex(currentIndex);
    } else if (currentScrollLeft > 0 && this.slider) {
      this.slider.scrollLeft = currentScrollLeft;
    }
  }

  onSectionUnload() {
    this.destroy();
  }

  onBlockLoad(event) {
    // Când se încarcă un block nou (adăugat, modificat), reinițializăm slider-ul
    // pentru a recalcula dimensiunile și controalele, păstrând slide-ul curent
    
    // Salvăm poziția curentă
    const currentIndex = this.currentIndex;
    const currentScrollLeft = this.slider ? this.slider.scrollLeft : 0;
    
    this.reinitialize();
    
    // Restaurăm poziția
    if (currentIndex > 0 && currentIndex < this.getTotalItems()) {
      this.scrollToIndex(currentIndex);
    } else if (currentScrollLeft > 0 && this.slider) {
      this.slider.scrollLeft = currentScrollLeft;
    }
  }
}

// Auto-register the custom element
if (!window.customElements.get("cos-slider")) {
  window.customElements.define("cos-slider", HorizontalScrollSlider);
}
